"use client";

import React from "react";
import { WPAPIPostGraphQL, WPTag } from "@/types";
import { compareDesc, isBefore, parseISO } from "date-fns";
import parse, { Element, domToReact, type DOMNode } from "html-react-parser";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Quote } from "lucide-react";
import IframeLightbox from "../IframeLightbox/IframeLightbox";
import { resolveIframeSrc } from "../LightboxProvider/LightboxProvider";
import WPPostRender from "../WPPostRender";

const lastDateToCheck = parseISO("2024-11-17");

type ParserChildren = Element["children"];

const toDomNodes = (children: ParserChildren): DOMNode[] => {
  return children as DOMNode[];
};

const findIframeNode = (children: ParserChildren): Element | null => {
  for (const child of children) {
    if (!(child instanceof Element)) continue;

    if (child.name === "iframe") return child;

    const found = findIframeNode(child.children);
    if (found) return found;
  }

  return null;
};


export const parseContent = (content: string): React.ReactNode => {
  return parse(content, {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element) {
        // Responsive embed wrapper — position:relative + padding-bottom aspect ratio hack
        if (
          domNode.name === "div" &&
          domNode.attribs?.style?.includes("padding-bottom")
        ) {
          const iframeNode = findIframeNode(domNode.children);
          if (iframeNode?.attribs?.src) {
            return (
              <IframeLightbox
                src={resolveIframeSrc(iframeNode.attribs.src)}
                title={iframeNode.attribs?.title}
                allow={iframeNode.attribs?.allow}
              />
            );
          }
        }

        if (domNode.name === "figure") {
          // YouTube — keep existing aspect-ratio wrapper
          if (domNode.attribs?.class?.includes("wp-block-embed-youtube")) {
            return (
              <div className="grid place-items-center aspect-w-16 aspect-h-9 w-full">
                {domToReact([domNode])}
              </div>
            );
          }

          // Any other figure containing an iframe (e.g. Stone widget) —
          // replace the whole figure so the constrained WordPress markup
          // (.iframe-container position:absolute, fixed widths) doesn't bleed in
          const iframeNode = findIframeNode(domNode.children);
          if (iframeNode?.attribs?.src) {
            return (
              <IframeLightbox
                src={resolveIframeSrc(iframeNode.attribs.src)}
                title={iframeNode.attribs?.title}
                allow={iframeNode.attribs?.allow}
              />
            );
          }
        }

        // Bare iframe (not inside a figure)
        if (domNode.name === "iframe" && domNode.attribs?.src) {
          return (
            <IframeLightbox
              src={resolveIframeSrc(domNode.attribs.src)}
              title={domNode.attribs?.title}
              allow={domNode.attribs?.allow}
            />
          );
        }

        if (domNode.name === "a") {
          return (
            <a
              href={domNode.attribs?.href}
              target={domNode.attribs?.target}
              rel={domNode.attribs?.rel}
              className="text-accent dark:text-indigo-400 underline underline-offset-2 hover:opacity-75 transition-opacity"
            >
              {domToReact(toDomNodes(domNode.children))}
            </a>
          );
        }

        if (domNode.name === "h2") {
          return (
            <h2 className="font-PlayfairDisplay text-stone-900 dark:text-stone-100">
              {domToReact(toDomNodes(domNode.children))}
            </h2>
          );
        }

        if (domNode.name === "h3") {
          return (
            <h3 className="font-PlayfairDisplay text-stone-900 dark:text-stone-100">
              {domToReact(toDomNodes(domNode.children))}
            </h3>
          );
        }

        // Check for wp-block-quote
        if (
          domNode.attribs &&
          domNode?.attribs?.class?.includes("wp-block-quote")
        ) {
          return (
            <blockquote className="custom-quote flex flex-col gap-3 border-l-4 border-stone-400 dark:border-stone-500 bg-stone-100 dark:bg-stone-800/60 text-stone-700 dark:text-stone-300 px-5 py-4 rounded-r-lg font-Lora italic text-lg leading-relaxed">
              <div className="not-italic mb-1">
                <Quote className="h-4 w-4 opacity-50" style={{ transform: "scaleX(-1)" }} />
              </div>
              {domToReact(toDomNodes(domNode.children))}
              <div className="flex w-full not-italic">
                <Quote className="ml-auto h-4 w-4 opacity-50" />
              </div>
            </blockquote>
          );
        }
      }

      // By default, return the unmodified node
      return undefined;
    },
  });
};

const sortPosts = (posts: WPAPIPostGraphQL[]): WPAPIPostGraphQL[] => {
  return posts.sort((a, b) => {
    const dateA = parseISO(a.node.actual_published_date || a.node.date);
    const dateB = parseISO(b.node.actual_published_date || b.node.date);
    if (isBefore(dateA, lastDateToCheck) && isBefore(dateB, lastDateToCheck)) {
      return compareDesc(dateA, dateB);
    } else if (isBefore(dateA, lastDateToCheck)) {
      return 1;
    } else if (isBefore(dateB, lastDateToCheck)) {
      return -1;
    } else {
      return compareDesc(dateA, dateB);
    }
  });
};

export const RenderPosts = ({ posts }: { posts: WPAPIPostGraphQL[] }) => {
  const sortedPosts = useMemo(() => sortPosts([...posts]), [posts]);
  const [visiblePosts, setVisiblePosts] = useState<{ [key: string]: boolean }>(
    {}
  );
  const lastVisiblePostRef = useRef(0);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;

    // Observe the "Read More" div for the next post
    const observeNext = (sortedPosts: WPAPIPostGraphQL[]) => {
      const nextPostIndex = lastVisiblePostRef.current + 1;
      const nextReadMore = document.querySelector(
        `#read-more-${nextPostIndex}`
      );

      if (observer) observer.disconnect();

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisiblePosts((prev) => ({
                ...prev,
                [sortedPosts[nextPostIndex].node.id]: true,
              }));
              lastVisiblePostRef.current = nextPostIndex;

              // Disconnect and observe the next "Read More" div
              observer?.disconnect();
              observeNext(sortedPosts); // Pass sortedPosts here
            }
          });
        },
        { threshold: 0.1 }
      );

      if (nextReadMore) observer.observe(nextReadMore);
    };

    const scrollHandler = () => {
      const viewportBottom = window.innerHeight + window.scrollY;
      const bufferZone = 100;

      for (
        let i = lastVisiblePostRef.current + 1;
        i < sortedPosts.length;
        i++
      ) {
        const element = document.querySelector(
          `#post-${sortedPosts[i].node.id}`
        );
        if (
          element &&
          element.getBoundingClientRect().top + bufferZone < viewportBottom
        ) {
          setVisiblePosts((prev) => ({
            ...prev,
            [sortedPosts[i].node.id]: true,
          }));
          lastVisiblePostRef.current = i;
          observeNext(sortedPosts); // Pass sortedPosts here
        }
      }
    };

    window.addEventListener("scroll", scrollHandler);
    observeNext(sortedPosts); // Start observing the first "Read More" div with sortedPosts

    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [sortedPosts]);

  return sortedPosts.map((post, index) => (
    <div
      className="post"
      id={`post-${post.node.id}`}
      key={post.node.id}
      style={{ opacity: index === 0 || visiblePosts[post.node.id] ? 1 : 0 }}
    >
      <WPPostRender
        hidden={index !== 0 && !visiblePosts[post.node.id]}
        post={post}
        index={index}
      />

      {index < sortedPosts.length - 1 && (
        <div
          id={`read-more-${index}`}
          className="read-more-trigger"
          style={{
            display: "block",
            padding: "10px",
            margin: "10px 0",
            textAlign: "center",
            cursor: "pointer",
            color: "white",
          }}
        ></div>
      )}
    </div>
  ));
};

export const renderPost = (post: WPAPIPostGraphQL) => {
  // Other code remains the same...
  return <WPPostRender post={post} index={0} />;
};

export const renderTagsCloud = (tags: WPTag[]) =>
  !tags.length ? (
    <div className="loading">Loading tags...</div>
  ) : (
    <div className="tag-cloud my-8">
      <h3 className="font-semibold text-lg mb-4 text-stone-800 dark:text-stone-200">
        Tags
      </h3>
      <div className="flex flex-wrap gap-3">
        {tags.map((tag) => (
          <a
            key={tag.id}
            href={`/blog/tag/${tag.name}`}
            className="text-sm font-medium text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-700 p-2 rounded-lg"
            style={{ fontSize: `${12 + tag.count * 2}px` }}
          >
            # {tag.name}
          </a>
        ))}
      </div>
    </div>
  );

export const renderComments = () => (
  <div className="comments-section mt-16 pt-8 border-t border-stone-300 dark:border-stone-700">
    <h3 className="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-200">
      Comments
    </h3>
    <p className="text-stone-600 dark:text-stone-400 mb-6">
      No comments yet. Be the first to comment!
    </p>
    <div className="flex flex-col space-y-4">
      <input
        type="text"
        placeholder="Write a comment..."
        className="p-4 border border-stone-300 dark:border-stone-600 rounded-lg bg-stone-50 dark:bg-stone-900 text-stone-900 dark:text-stone-100"
      />
      <Button className="w-full">Submit Comment</Button>
    </div>
  </div>
);
